import api, { ApiResponse } from './api';

export interface LogEntry {
  id: number;
  user_id: number;
  log_type: 'stream' | 'panel' | 'error';
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  context: string | null;
  created_at: string;
}

export interface LogsResponse {
  logs: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ApiLogEntry {
  ts: string;
  id?: string;
  userId: number;
  api: string;
  method: string;
  url: string;
  requestHeaders?: Record<string, unknown>;
  requestBody?: unknown;
  status?: number;
  responseHeaders?: Record<string, unknown>;
  responseBody?: unknown;
  error?: string;
  durationMs?: number;
  context?: { channelName?: string; eventName?: string; competitionName?: string; assetId?: string };
}

export interface ApiLogsResponse {
  entries: ApiLogEntry[];
  page: number;
  limit: number;
  hasMore: boolean;
}

export const logsService = {
  async getApiLog(id: string, admin = false): Promise<ApiLogEntry> {
    const response = await api.get<ApiResponse<ApiLogEntry>>(`${admin ? '/logs/admin/api' : '/logs/api'}/${encodeURIComponent(id)}`);
    return response.data.data;
  },

  async listApiLogs(
    params: { search?: string; errors_only?: number; hide_polling?: number; page?: number; limit?: number; user_id?: number },
    admin = false
  ): Promise<ApiLogsResponse> {
    const response = await api.get<ApiResponse<ApiLogsResponse>>(admin ? '/logs/admin/api' : '/logs/api', { params });
    return response.data.data;
  },

  async list(params?: { log_type?: string; level?: string; page?: number; limit?: number }): Promise<LogsResponse> {
    const response = await api.get<ApiResponse<LogsResponse>>('/logs', { params });
    return response.data.data;
  },

  async searchLogs(params: { search?: string; log_type?: string; level?: string; page?: number; limit?: number }): Promise<LogsResponse> {
    const response = await api.get<ApiResponse<LogsResponse>>('/logs/search', { params });
    return response.data.data;
  },

  async getStreamLogs(params?: { page?: number; limit?: number }): Promise<LogsResponse> {
    const response = await api.get<ApiResponse<LogsResponse>>('/logs/stream', { params });
    return response.data.data;
  },

  async getPanelLogs(params?: { page?: number; limit?: number }): Promise<LogsResponse> {
    const response = await api.get<ApiResponse<LogsResponse>>('/logs/panel', { params });
    return response.data.data;
  },

  async downloadLogs(params?: { log_type?: string }): Promise<Blob> {
    const response = await api.get('/logs/download', { params, responseType: 'blob' });
    return response.data;
  },

  async clearAll(): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<null> & { message: string }>('/logs/clear');
    return { message: response.data.message || 'Logs cleared' };
  },

  async clearStreamLogs(): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<null> & { message: string }>('/logs/clear/stream');
    return { message: response.data.message || 'Stream logs cleared' };
  },

  async clearPanelLogs(): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<null> & { message: string }>('/logs/clear/panel');
    return { message: response.data.message || 'Panel logs cleared' };
  },
};
